import type { FC } from 'react';
import { useMemoOne } from 'use-memo-one';
import { Tooltip } from 'src/components/overlay/tooltip';
import { cn } from 'src/utils/cn';

export type NumberType =
  | '0-decimals'
  | '2-decimals'
  | '4-decimals'
  | 'exact-decimals';

type FormatValueProps = {
  value: number | string;
  label: string;
  hideLabel?: boolean;
  /**
   * Usually only used in tables and only if you need to save space.
   */
  stacked?: boolean;
  /**
   * Should be used in overview pages (usually tables).
   */
  smallLabel?: boolean;
  /**
   * In overview pages you should consider using `'0-decimals'` or `'2-decimals'`.
   * In rare cases you might need to use `'4-decimals'`.
   * On details pages `'exact-decimals'` is the right choice. (It is also the default.)
   */
  type?: NumberType;
};

const zeroDecimalsFormat: Intl.NumberFormatOptions = {
  maximumFractionDigits: 0,
};

const twoDecimalsFormat: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2, // to make comparing different values more easily (espacially inside tables)
  maximumFractionDigits: 2,
};

const fourDecimalsFormat: Intl.NumberFormatOptions = {
  minimumFractionDigits: 4, // to make comparing different values more easily (espacially inside tables)
  maximumFractionDigits: 4,
};

export const exactFormat: Intl.NumberFormatOptions = {
  maximumFractionDigits: 20,
};

const format: Record<NumberType, Intl.NumberFormatOptions> = {
  '0-decimals': zeroDecimalsFormat,
  '2-decimals': twoDecimalsFormat,
  '4-decimals': fourDecimalsFormat,
  'exact-decimals': exactFormat,
};

export const formatNumber = (
  value: number,
  type: NumberType = 'exact-decimals'
) => value.toLocaleString('en-GB', format[type]);

export const FormatValue: FC<FormatValueProps> = ({
  value,
  label,
  smallLabel = false,
  hideLabel = false,
  stacked = false,
  type = 'exact-decimals',
}) => {
  const result = useMemoOne(() => {
    const number = typeof value === 'string' ? parseFloat(value) : value;
    const formatted = formatNumber(number, type);
    const exact = number.toLocaleString('en-GB', {
      ...format[type],
      maximumFractionDigits: 20,
    });

    if (formatted !== exact) {
      return { isRounded: true, formatted, exact };
    } else {
      return { isRounded: false, formatted };
    }
  }, [value, type]);

  const labelSize = hideLabel ? null : (
    <>
      {stacked && <br />}
      {smallLabel ? <small> {label}</small> : <> {label}</>}
    </>
  );

  return (
    <>
      {result.isRounded ? (
        <>
          <Tooltip
            content={
              <small>
                <strong>Exact Number:</strong>
                <div className="[word-wrap:break-word]">
                  {result.exact} {label}
                </div>
              </small>
            }
          >
            {(targetProps) => (
              <span
                {...targetProps}
                className={cn(
                  `
                    relative
                    after:absolute after:top-sm after:-right-2xs
                    after:content-['…']
                  `,
                  `
                    after:text-label-sm after:font-label-semibold
                    after:text-foreground-primary-inverted
                  `,
                  `
                    legacy:after:text-[12px] legacy:after:leading-[17.1429px]
                    legacy:after:font-[800]
                    legacy:after:text-legacy-brand-secondary
                  `
                )}
              >
                {result.formatted}
              </span>
            )}
          </Tooltip>
          {labelSize}
        </>
      ) : (
        <>
          {result.formatted}
          {labelSize}
        </>
      )}
    </>
  );
};
